import { AppData, Colors, Lightning, Router } from '@lightningjs/sdk';
import { EpgChannel, EpgProgram } from 'types/api/media';
import OnDemandBadge from 'components/common/OnDemandBadge';
import RatingBadge, { EXTRA_SMALL_SIZE } from 'components/common/RatingBadge';
import { translate } from 'support/translate';
import { getFontFaceFromStyle, withSeparator } from 'support/textUtils';
import { timeTo12HourClockHoursMinutes } from 'support/timeUtils';
import EpgModalButton from 'components/pages/epg/EpgModalButton';
import ActionsList from 'components/common/ActionsList';
import { RoutePath } from 'config/routes';
import { constants } from 'aliases';
import { debounce, getFocusDepth } from 'support/generalUtils';
import TextLabel from 'components/common/TextLabel';
import { PageId } from 'types/pageId';
import {
  reportChannelEngagement,
  reportMyStuffAddRemove,
} from 'services/analytics/reportingServicePage';

export interface EpgModalTemplateSpec extends Lightning.Component.TemplateSpec {
  channel: EpgChannel;
  program: EpgProgram;

  BackgroundOverlay: Lightning.textures.RectangleTexture;
  Modal: {
    Background: Lightning.textures.ImageTexture;
    Content: {
      ChannelMetadata: typeof TextLabel;
      Title: typeof TextLabel;
      ProgramMetadata: {
        Subtitle: typeof TextLabel;
        RatingBadge: typeof RatingBadge;
        OnDemand: typeof OnDemandBadge;
      };
      Description: typeof TextLabel;
    };
    ButtonList: typeof ActionsList;
  };
}

const MODAL_X = 1159;
const MODAL_Y = 104;
const MODAL_WIDTH = 666;
const MODAL_HEIGHT = 892;

const PROGRAM_IMAGE_WIDTH = MODAL_WIDTH;
const PROGRAM_IMAGE_HEIGHT = 375;

const CONTENT_X = 41;
const CONTENT_MAX_WIDTH = 611;
const DEFAULT_CONTENT_PADDING = 22;

const PROGRAM_METADATA_MARGIN_LEFT = 2;
const PROGRAM_METADATA_MARGIN_RIGHT = 7;
const PROGRAM_METADATA_HEIGHT = EXTRA_SMALL_SIZE.HEIGHT; // Match the size of the rating badge to avoid pixel rendering cutoffs

const BUTTON_WIDTH = MODAL_WIDTH;
const BUTTON_HEIGHT = 87;

const TEXT_DEFAULT_FONT_SIZE = 18;

const ICON_SIZE = 26;
const ICON_SIZE_SMALL = 18;

const SERVICE_DEBOUNCE = 500;

export default class EpgModal
  extends Lightning.Component<EpgModalTemplateSpec>
  implements Lightning.Component.ImplementTemplateSpec<EpgModalTemplateSpec>
{
  private _channel?: EpgChannel;
  private _program?: EpgProgram;

  private _Modal = this.getByRef('Modal')!;
  private _Background = this._Modal.getByRef('Background')!;
  private _Content = this._Modal.getByRef('Content')!;
  private _ButtonList = this._Modal.getByRef('ButtonList')!;

  private _ChannelMetadata = this._Content.getByRef('ChannelMetadata')!;
  private _Title = this._Content.getByRef('Title')!;
  private _ProgramMetadata = this._Content.getByRef('ProgramMetadata')!;
  private _Description = this._Content.getByRef('Description')!;

  private _Subtitle = this._ProgramMetadata.getByRef('Subtitle')!;
  private _RatingBadge = this._ProgramMetadata.getByRef('RatingBadge')!;
  private _OnDemand = this._ProgramMetadata.getByRef('OnDemand')!;

  private _isLoggedIn = false;
  private _isMyStuffAdded = false;
  private cwServiceDebounce = debounce(() => {
    this.handleFavorite();
  }, SERVICE_DEBOUNCE);

  get title() {
    const { description, ratings, subtitle, title } = this._program ?? {};
    const channelMetadata = this._ChannelMetadata.text?.text ?? '';
    const onDemand = this._OnDemand.visible ? translate('global.onDemand') : '';

    return [
      channelMetadata,
      title ?? '',
      subtitle ?? '',
      ratings ?? '',
      onDemand,
      description ?? '',
    ];
  }

  set channel(channel: EpgChannel) {
    this._channel = channel;

    this.updateChannelMetadata();
  }

  set program(program: EpgProgram) {
    this._program = program;

    this._Background.patch({ src: program.images.detailsUrl ?? '' });
    this._Title.patch({ text: program.title ?? '' });
    this._Description.patch({ text: program.description ?? '' });

    this._Subtitle.patch({ text: program.subtitle ?? '' });
    this._RatingBadge.patch({ rating: program.ratings ?? '' });
    this._OnDemand.patch({ visible: program.isOnDemand ?? false });

    this.updateChannelMetadata();
    this.updateButtons();
  }

  static override _template(): Lightning.Component.Template<EpgModalTemplateSpec> {
    return {
      BackgroundOverlay: {
        rect: true,
        w: 1920,
        h: 1080,
        color: Colors('black').alpha(0.8).get(),
        collision: true,
        zIndex: constants.ui.zIndex.foregroundLayer + 1,
      },
      Modal: {
        x: MODAL_X,
        y: MODAL_Y,
        rect: true,
        color: Colors('appBackground').get(),
        w: MODAL_WIDTH,
        h: MODAL_HEIGHT,
        zIndex: constants.ui.zIndex.foregroundLayer + 1,
        Background: {
          rect: true,
          w: PROGRAM_IMAGE_WIDTH,
          h: PROGRAM_IMAGE_HEIGHT,
        },
        Content: {
          flex: {
            direction: 'column',
            paddingTop: DEFAULT_CONTENT_PADDING,
          },
          x: CONTENT_X,
          y: PROGRAM_IMAGE_HEIGHT,
          ChannelMetadata: {
            type: TextLabel,
            flexItem: { marginBottom: DEFAULT_CONTENT_PADDING },
            text: { fontSize: TEXT_DEFAULT_FONT_SIZE },
          },
          Title: {
            type: TextLabel,
            flexItem: { marginBottom: 4 },
            w: CONTENT_MAX_WIDTH,
            text: {
              fontSize: 30,
              fontFace: getFontFaceFromStyle('bold'),
              textColor: Colors('text').get(),
              maxLines: 1,
              maxLinesSuffix: translate('global.defaultMaxLineSuffix'),
            },
          },
          ProgramMetadata: {
            flexItem: { marginBottom: 20 },
            h: PROGRAM_METADATA_HEIGHT,
            flex: {
              direction: 'row',
              alignItems: 'center',
            },
            w: CONTENT_MAX_WIDTH,
            Subtitle: {
              type: TextLabel,
              flexItem: { marginRight: PROGRAM_METADATA_MARGIN_RIGHT },
              text: {
                lineHeight: PROGRAM_METADATA_HEIGHT,
                h: PROGRAM_METADATA_HEIGHT,
                fontSize: TEXT_DEFAULT_FONT_SIZE,
                textColor: Colors('text').alpha(0.65).get(),
                maxLines: 1,
                maxLinesSuffix: translate('global.defaultMaxLineSuffix'),
              },
            },
            RatingBadge: {
              flexItem: {
                marginLeft: PROGRAM_METADATA_MARGIN_LEFT,
                marginRight: PROGRAM_METADATA_MARGIN_RIGHT,
              },
              type: RatingBadge,
              size: 'extraSmall',
            },
            OnDemand: {
              type: OnDemandBadge,
              visible: false,
              w: 84,
              h: PROGRAM_METADATA_HEIGHT,
            },
          },
          Description: {
            type: TextLabel,
            w: CONTENT_MAX_WIDTH,
            text: {
              fontSize: TEXT_DEFAULT_FONT_SIZE,
              lineHeight: 21,
              textColor: Colors('text').get(),
              maxLines: 4,
              maxLinesSuffix: translate('global.defaultMaxLineSuffix'),
            },
          },
        },
        ButtonList: {
          type: ActionsList,
          flex: {
            direction: 'column',
          },
          y: MODAL_HEIGHT,
          mountY: 1,
          spacing: 0,
          items: [],
          signals: {
            $dismissModal: true,
            $addToMyStuff: true,
            $goToShow: true,
          },
          zIndex: constants.ui.zIndex.foregroundLayer + 1,
        },
      },
    };
  }

  override _active() {
    // The program subtitle can overflow, preventing the rating and on demand badges
    // from appearing. Added this custom logic to set the subtitle width such that
    // the badges have enough space to appear
    this._ProgramMetadata.onAfterUpdate = () => {
      const subtitleMargin = this._Subtitle.flexItem?.marginRight ?? 0;
      const ratingMargin = this._RatingBadge.flexItem?.marginRight ?? 0;

      const containerW = this._ProgramMetadata.w;
      const ratingW = this._RatingBadge.finalW;
      const onDemandW = this._OnDemand.visible ? this._OnDemand.finalW : 0;

      const subtitleW =
        containerW - subtitleMargin - ratingW - ratingMargin - onDemandW;
      this._Subtitle.patch({ text: { wordWrapWidth: subtitleW } });

      this._ProgramMetadata.onAfterUpdate = undefined;
    };

    this._isLoggedIn = AppData?.loginRadiusService?.isLoggedIn() ?? false;

    if (this._isLoggedIn) this.checkFavorited();

    this.updateButtons();
  }

  override _inactive() {
    this._ProgramMetadata.onAfterUpdate = undefined;
  }

  override _getFocused() {
    return this._ButtonList;
  }

  override _handleLeft() {
    // Do not focus on navbar
    return true;
  }

  override _handleBack() {
    this.$dismissModal();
  }

  private async checkFavorited() {
    if (!this._channel) return;

    try {
      const favoriteCheckPromise =
        await AppData?.personaService?.getFavoriteChannelItem(this._channel.id);
      this._isMyStuffAdded = !!favoriteCheckPromise?.content_title;
    } catch (error) {
      // TODO: handle error
      this._isMyStuffAdded = false;
    } finally {
      this._ButtonList
        .getItemByRef('AddToMyStuffButton')
        ?.patch({ visible: true });
    }
    this.updateMyStuffButton();
  }

  private updateButtons() {
    const items: Lightning.Element.PatchTemplate[] = [];

    if (this._program?.seriesSlug) {
      items.push({
        ref: 'GoToShowButton',
        action: '$goToShow',
        passSignals: { $goToShow: true },
        signals: { $onHover: true },
        type: EpgModalButton,
        w: BUTTON_WIDTH,
        h: BUTTON_HEIGHT,
        titleKey: 'epg.modal.goToDetails',
        icon: {
          focused: 'static/images/arrow-right-dark.svg',
          unfocused: 'static/images/arrow-right-light.svg',
        },
        iconSizing: {
          width: ICON_SIZE,
          height: ICON_SIZE,
        },
        zIndex: constants.ui.zIndex.foregroundLayer + 1,
      });
    }

    if (this._isLoggedIn) {
      items.push({
        visible: false,
        ref: 'AddToMyStuffButton',
        action: '$addToMyStuff',
        passSignals: { $addToMyStuff: true },
        signals: { $onHover: true },
        type: EpgModalButton,
        w: BUTTON_WIDTH,
        h: BUTTON_HEIGHT,
        iconSizing: {
          width: ICON_SIZE,
          height: ICON_SIZE,
        },
        zIndex: constants.ui.zIndex.foregroundLayer + 1,
      });
    }

    items.push({
      ref: 'BackToGuideButton',
      action: '$dismissModal',
      passSignals: { $dismissModal: true },
      signals: { $onHover: true },
      type: EpgModalButton,
      w: BUTTON_WIDTH,
      h: BUTTON_HEIGHT,
      titleKey: 'epg.backToGuide',
      icon: {
        focused: 'static/images/epg/back-to-channel-guide-icon-focused.svg',
        unfocused: 'static/images/epg/back-to-channel-guide-icon-unfocused.svg',
      },
      iconSizing: {
        width: ICON_SIZE,
        height: ICON_SIZE_SMALL,
      },
      zIndex: constants.ui.zIndex.foregroundLayer + 1,
    });

    this._ButtonList.patch({ items });
  }

  private async handleFavorite() {
    try {
      if (this._isMyStuffAdded) {
        await AppData?.personaService?.addToFavoriteChannels(this._channel!);
      } else {
        await AppData?.personaService?.removeFromFavoriteChannels([
          this._channel!.id,
        ]);
      }
    } catch (error) {
      // TODO: handle error
    }
  }

  private $addToMyStuff() {
    if (!this._channel) return;

    this._isMyStuffAdded = !this._isMyStuffAdded;
    this.signal('showMyStuffToast', this._isMyStuffAdded, this._channel!.title);
    this.updateMyStuffButton();

    reportChannelEngagement(
      this._channel.title,
      this._isMyStuffAdded ? 'Add Favorite Channel' : 'Remove Favorite Channel',
    );

    reportMyStuffAddRemove({
      actionType: this._isMyStuffAdded ? 'Add' : 'Remove',
      type: this._channel!.title,
      pageId: PageId.EPG,
      rowName: 'Channels',
    });

    this.cwServiceDebounce();
  }

  private updateMyStuffButton() {
    const titleKey = this._isMyStuffAdded
      ? 'myStuff.removeChannelFromMyStuff'
      : 'myStuff.addChannelToMyStuff';
    const icon = {
      focused: this._isMyStuffAdded
        ? 'static/images/x-black.svg'
        : 'static/images/plus-black.svg',
      unfocused: this._isMyStuffAdded
        ? 'static/images/check-white.svg'
        : 'static/images/plus-white.svg',
    };
    this._ButtonList.getItemByRef('AddToMyStuffButton')?.patch({
      titleKey,
      icon,
    });
    this.fireAncestors(
      '$announcerRefresh',
      getFocusDepth(this.application, this._ButtonList),
    );
  }

  private updateChannelMetadata() {
    const channelMetadata: string[] = [];

    if (this._program) {
      const startDate = new Date(this._program.startTime);
      const startTime = timeTo12HourClockHoursMinutes(startDate);
      channelMetadata.push(startTime);
    }
    if (this._channel) {
      const channelName = this._channel.title;
      channelMetadata.push(channelName);
    }

    const channelMetadataText = withSeparator(...channelMetadata);
    this._ChannelMetadata.patch({ text: channelMetadataText });
  }

  private $goToShow() {
    const { seriesType, seriesSlug } = this._program!;
    const basePath =
      seriesType === 'series'
        ? RoutePath.SERIES_DETAIL
        : RoutePath.MOVIE_DETAIL;

    // Fire analytics event
    if (this._channel) {
      reportChannelEngagement(this._channel.title, 'Go to Details Page');
    }

    Router.navigate(`${basePath}/${seriesSlug}`);
  }

  private $dismissModal() {
    this.resetUI();
    this.signal('dismissModal');
  }

  private resetUI() {
    this._Background.patch({ src: '' });
    this._ChannelMetadata.patch({ text: '' });
    this._Title.patch({ text: '' });
    this._Description.patch({ text: '' });

    this._Subtitle.patch({ text: '' });
    this._RatingBadge.patch({ rating: '' });
    this._OnDemand.patch({ visible: false });

    this._ButtonList.clearActions();
  }
}
